/**
 * Route-level RBAC guard, independent from the sidebar's `access` metadata (nav-config.ts) since
 * it must also cover routes outside the sidebar (e.g. /profile, /leave). Used by AppLayout's
 * redirect effect after the existing isDevRestricted/monetOnly special cases. Admin always
 * passes; unknown/unlisted paths default-deny.
 *
 * The per-page rule and role logic lives in @system/shared (canOpenPage) so the API page gate runs
 * the same predicate; this file adds the web-only global on/off switch (disabledNav) and the
 * redirect targets.
 */
import { canOpenPage, isPageBlocked, type AccessProfile } from '@system/shared'
import { disabledNavFor } from './nav-config'

// /exam/take + /exam/result đi cùng /exam/my: đó là chính bài thi mà "Bài thi của tôi" mở ra.
// Thiếu hai đường này, nhân viên bấm "Làm bài" bị đẩy về trang chủ mà không có lỗi nào (18/08).
const ALWAYS_ALLOWED = new Set(['/', '/profile', '/leave', '/exam/my', '/exam/take', '/exam/result'])

/**
 * A landing page, unless it cannot be opened — switched off by SPN004, or blocked for this person
 * by a per-page rule — then '/' (always on, never a rule or toggle target) so a closed landing page
 * cannot loop the redirect straight back to itself.
 */
function landingOrRoot(path: string, access: AccessProfile): string {
  return disabledNavFor(path, access) || isPageBlocked(path, access) ? '/' : path
}

/** The page a caller lands on once none of the pinned-scope or per-role rules keep them where they are. */
function roleLanding(access: AccessProfile): string {
  return access.isUa
    ? '/marketing'
    : access.isMonet
      ? '/monetization'
      : access.isDev || access.hasDevApps
        ? '/developer'
        : access.isHr
          ? '/hr/leave'
          : '/'
}

/** Where a dev-restricted or monetOnly account's own pinned scope sends it when kicked off a page. */
function pinnedLanding(access: AccessProfile): string {
  return access.isDevRestricted ? '/developer' : '/monetization/admob-mediation'
}

/**
 * Where a restricted scope has to be sent when it lands somewhere it may not be, or `null` to stay.
 *
 * The two pinned scopes — an external report-only viewer and a monetization-only account — used to be
 * bounced to their one page by path prefix alone. That is right for the pages they were never given,
 * and wrong the moment one of them is granted a page by name: the grant said yes and the bounce said
 * no, so the grant did nothing. Asking `canVisit` first means a named permission is honoured without
 * widening anything else.
 */
export function redirectFor(pathname: string, access: AccessProfile): string | null {
  // Sidebar toggle (user decision 2026-09-28), checked FIRST — ahead of every exemption below
  // (personalAllowed, the raw /developer* / /monetization* prefix a restricted scope is pinned
  // to, namedGrant). None of those may reach a page SPN004 switched off; canVisit's own
  // disabledNavFor check would catch a namedGrant lookup, but the raw prefix checks do not run
  // canVisit at all, so a restricted account visiting its own pinned area directly bypassed the
  // block entirely before this check existed.
  // A per-page rule block (user decision 29/09/2026) closes the page the same way, before the
  // pinned-scope and personal-page exemptions below — admins included.
  if (disabledNavFor(pathname, access) || isPageBlocked(pathname, access)) {
    return landingOrRoot(
      access.isDevRestricted || access.monetOnly ? pinnedLanding(access) : roleLanding(access),
      access,
    )
  }

  // Internal employees keep their personal pages (dashboard, profile, leave, exam) even when
  // dev/monet-restricted — those are the sidebar-footer links; external viewers stay pinned.
  const personalAllowed =
    access.isEmployee &&
    (pathname === '/' ||
      pathname === '/profile' ||
      pathname === '/leave' ||
      pathname.startsWith('/exam'))

  /**
   * A page this account was given by name rather than by role.
   *
   * The always-allowed pages are deliberately excluded: they are open to everyone signed in, and
   * letting them through here would quietly unpin the external accounts this rule exists to pin.
   */
  const namedGrant = !ALWAYS_ALLOWED.has(pathname) && canVisit(pathname, access)

  if (access.isDevRestricted) {
    const allowed =
      pathname === '/developer' || pathname.startsWith('/developer/') || personalAllowed || namedGrant
    return allowed ? null : landingOrRoot('/developer', access)
  }
  if (access.monetOnly) {
    const allowed = pathname.startsWith('/monetization') || personalAllowed || namedGrant
    return allowed ? null : landingOrRoot('/monetization/admob-mediation', access)
  }
  if (canVisit(pathname, access)) return null
  // Role landing page — each target always passes canVisit for its own role, so this cannot loop,
  // except when SPN004 switched the target itself off; landingOrRoot falls back to '/' then.
  return landingOrRoot(roleLanding(access), access)
}

export function canVisit(pathname: string, access: AccessProfile): boolean {
  // Sidebar toggle (user decision 2026-09-28): checked before every other rule, including the
  // app-wide admin shortcut — a switched-off page is blocked for everyone except the one eCode that
  // may still test it (disabledNavFor already carves out canToggleNav).
  if (disabledNavFor(pathname, access)) return false
  return canOpenPage(pathname, access)
}
